import { DeleteOutlined, ExpandMoreRounded, WarningAmberRounded } from "@mui/icons-material";
import { Box, Collapse, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import MetaChip from "../../../../../components/UIToolkit/Badges/MetaChip";
import { InlineEditableText } from "../../../../../components/UIToolkit/Inputs/InlineEditableText";
import SystemButton from "../../../../../components/UIToolkit/SystemButton/SystemButton";
import { FONT_SIZE, INK, MOTION, NEUTRAL } from "../../../../../theme/tokens";
import type { AutoApproveRule } from "../../../../../utils/models";
import { AutoApproveRuleFields } from "./AutoApproveRuleFields";
import type { JobCatalogState } from "./autoApproveConsts";
import { approvesNoOne, ruleSummaryOf } from "./ruleSummary";

const L = "department.config.staffApp.autoApprove.list";
const F = "department.config.staffApp.autoApprove.fields";
const ORDER_SIZE = 24;
/** Width (px) of the brand edge marking the open rule. */
const RULE_EDGE = 3;
/** Widest the in-place name field grows, in theme spacing units — a name, not a sentence. */
const NAME_WIDTH = 45;
/** Narrowest it gets, so a short name stays an easy click target. */
const NAME_MIN_WIDTH = "8ch";

type Props = {
	rule: AutoApproveRule;
	index: number;
	jobCatalog: JobCatalogState;
	open: boolean;
	onToggle: () => void;
	onChange: (patch: Partial<AutoApproveRule>) => void;
	onDelete: () => void;
};

/** One rule as a line — its place in the order, its name and what it approves — that opens to edit its conditions. */
export const AutoApproveRuleRow: FC<Props> = ({ rule, index, jobCatalog, open, onToggle, onChange, onDelete }) => {
	const { t, i18n } = useTranslation();
	const summaryId = `auto-approve-rule-summary-${index}`;
	const panelId = `auto-approve-rule-fields-${index}`;
	const untitled = t(`${L}.untitled`, { number: index + 1 });
	const ruleName = rule.name || untitled;
	return (
		<Box
			data-testid={`dept-config-auto-approve-row-${index}`}
			sx={(theme) => ({
				"& + &": { borderTop: `1px solid ${NEUTRAL[200]}` },
				// The open rule is marked by a brand edge, not a grey panel, so its controls sit on the same white as its fields.
				boxShadow: open ? `inset ${RULE_EDGE}px 0 0 ${theme.palette.primary.main}` : "none",
				transition: `box-shadow ${MOTION.DURATION.COLOR_SHIFT}ms`,
				"@media (prefers-reduced-motion: reduce)": { transition: "none" },
			})}
		>
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: { xs: `${ORDER_SIZE}px minmax(0, 1fr)`, sm: `${ORDER_SIZE}px minmax(0, 1fr) auto` },
					alignItems: "center",
					columnGap: 1.5,
					rowGap: { xs: 0.5, sm: 0 },
					px: 1.5,
					py: 1.25,
				}}
			>
				<Box
					aria-hidden
					sx={{
						width: ORDER_SIZE,
						height: ORDER_SIZE,
						borderRadius: "50%",
						display: "grid",
						placeItems: "center",
						fontSize: FONT_SIZE.CAPTION,
						fontWeight: 700,
						color: "primary.dark",
						backgroundColor: "primary.light",
					}}
				>
					{index + 1}
				</Box>
				<Box sx={{ minWidth: 0 }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
						{open ? (
							<InlineEditableText
								value={rule.name}
								onCommit={(name) => onChange({ name })}
								revertOnEscape
								ariaLabel={t(`${F}.name.label`)}
								placeholder={untitled}
								dataTestId={`dept-config-auto-approve-name-${index}`}
								// Pulled out by its own inset to sit on the collapsed name's edge, and sized to its text so the chips stay beside it.
								sx={{
									fontSize: FONT_SIZE.EMPHASIS,
									fontWeight: 700,
									mx: -0.75,
									fieldSizing: "content",
									minWidth: NAME_MIN_WIDTH,
									maxWidth: (theme) => theme.spacing(NAME_WIDTH),
								}}
							/>
						) : (
							<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 700, color: rule.name === "" ? INK.MUTED : INK.PRIMARY }}>
								{rule.name === "" ? untitled : rule.name}
							</Typography>
						)}
						{approvesNoOne(rule) && (
							<MetaChip
								size="small"
								tone="warning"
								icon={<WarningAmberRounded />}
								label={t(`${L}.approves_no_one`)}
								tooltip={t(`${L}.approves_no_one_tip`)}
								dataTestId={`dept-config-auto-approve-no-ot-${index}`}
							/>
						)}
					</Box>
					<Typography
						id={summaryId}
						data-testid={`dept-config-auto-approve-logic-${index}`}
						sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.SECONDARY, mt: 0.25 }}
					>
						{ruleSummaryOf(t, rule, i18n.language)}
					</Typography>
				</Box>
				<Box sx={{ display: "flex", alignItems: "center", gap: 0.25, gridColumn: { xs: 2, sm: "auto" }, justifySelf: "end" }}>
					<SystemButton
						buttonVariant="borderless"
						size="small"
						noFullWidthMobile
						onClick={onToggle}
						aria-expanded={open}
						aria-controls={open ? panelId : undefined}
						aria-label={t(`${L}.${open ? "doneRule" : "editRule"}`, { name: ruleName })}
						aria-describedby={summaryId}
						data-testid={`dept-config-auto-approve-toggle-${index}`}
					>
						<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
							{open ? t(`${L}.done`) : t(`${L}.edit`)}
							<ExpandMoreRounded
								fontSize="small"
								sx={{
									transform: open ? "rotate(180deg)" : "none",
									transition: `transform ${MOTION.DURATION.ENTRANCE_MD}ms ${MOTION.EASE_OUT}`,
									"@media (prefers-reduced-motion: reduce)": { transition: "none" },
								}}
							/>
						</Box>
					</SystemButton>
					<SystemButton
						buttonVariant="borderless"
						size="small"
						noFullWidthMobile
						leadingIcon={<DeleteOutlined fontSize="small" />}
						onClick={onDelete}
						tooltip={t("department.config.staffApp.autoApprove.deleteSet")}
						aria-label={t(`${L}.deleteRule`, { name: ruleName })}
						data-testid={`dept-config-auto-approve-delete-${index}`}
					/>
				</Box>
			</Box>
			<Collapse in={open} unmountOnExit>
				<Box id={panelId} sx={(theme) => ({ px: 1.5, pb: 1, paddingInlineStart: { sm: `calc(${ORDER_SIZE}px + ${theme.spacing(3)})` } })}>
					<AutoApproveRuleFields rule={rule} index={index} jobCatalog={jobCatalog} onChange={onChange} />
				</Box>
			</Collapse>
		</Box>
	);
};
